import './styles.scss';

import { DragDropProvider, DragItem } from './dragAndDrop';
import { QuestionnaireCategory, QuestionnaireQuestion } from '@/models/gen/graphql';
import { ReactElement, useMemo } from 'react';

import HasPermission from '@/components/HasPermission';
import { QuestionsTableHeader } from './QuestionsTableHeader';
import { QuestionsTableRow } from './QuestionsTableRow';
import Table from '@/common/Table';
import { getProperty } from '@/utils';
import { handleError } from '@/utils/custom';
import { updateQuestion } from '@/features/Settings/api/services/training/updateQuestionnaireQuestions';
import { useQuestionsState } from '@/features/Settings/pages/Questions';

type QuestionsTableProps = {
  categories: QuestionnaireCategory[];
  questions: QuestionnaireQuestion[];
  onEdit: (id: string) => void;
  onDelete: (id: string) => void;
};

export const QuestionsTable = ({ categories, questions, onEdit, onDelete }: QuestionsTableProps): ReactElement => {
  const [{ sortBy, sortDirection, fetch }] = useQuestionsState(({ state }) => [
    {
      sortBy: state.table.sorting.column,
      sortDirection: state.table.sorting.direction,
      fetch: state.fetch,
    },
  ]);

  const categoriesById = useMemo(
    (): Record<string, QuestionnaireCategory> =>
      categories.reduce<Record<string, QuestionnaireCategory>>((map, category) => ({ ...map, [category.id]: category }), {}),
    [categories]
  );

  // Dragging is only meaningful against the item's stored order - once a column sort is applied,
  // row position no longer reflects sortOrder, so reordering is disabled for the duration of that sort.
  const isDraggable = HasPermission.check('allowEditTrainingSettings') && (!sortBy || !sortDirection);

  const sortedQuestions = useMemo((): QuestionnaireQuestion[] => {
    if (!sortBy || !sortDirection)
      return [...questions].sort((a, b) => {
        const categoryDiff =
          (categoriesById[a.questionnaireCategoryId]?.sortOrder ?? 0) - (categoriesById[b.questionnaireCategoryId]?.sortOrder ?? 0);
        return categoryDiff !== 0 ? categoryDiff : (a.sortOrder ?? 0) - (b.sortOrder ?? 0);
      });
    return [...questions].sort((a: QuestionnaireQuestion, b: QuestionnaireQuestion): number => {
      const aValue = (getProperty(sortBy, a) ?? '').toLowerCase();
      const bValue = (getProperty(sortBy, b) ?? '').toLowerCase();
      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [questions, sortBy, sortDirection, categoriesById]);

  const dragItems = useMemo(
    (): DragItem[] => sortedQuestions.map((question) => ({ id: question.id, group: question.questionnaireCategoryId })),
    [sortedQuestions]
  );

  const handleReorder = async (newItems: DragItem[]): Promise<void> => {
    const changedIndex = newItems.findIndex((item, index) => item.id !== dragItems[index]?.id);
    if (changedIndex === -1) return;

    const categoryId = newItems[changedIndex].group;
    const updates = newItems.filter((item) => item.group === categoryId).map((item, index) => ({ id: item.id, sortOrder: index + 1 }));

    try {
      await updateQuestion(updates);
      fetch();
    } catch (err) {
      handleError(err, { notification: { title: 'Reorder Training Item' } });
    }
  };

  return (
    <DragDropProvider items={dragItems} onReorder={handleReorder}>
      <Table className="QuestionsTable" header={<QuestionsTableHeader />} showControls>
        {!sortedQuestions.length && (
          <Table.Row virtualize={false} style={{ gridTemplateColumns: '1fr' }}>
            <Table.Cell className="text-center">
              <i className="fa fa-inbox" /> <span>No Data</span>
            </Table.Cell>
          </Table.Row>
        )}
        {sortedQuestions.map(
          (question, index): ReactElement => (
            <QuestionsTableRow
              key={question.id}
              question={question}
              category={categoriesById[question.questionnaireCategoryId]}
              index={index}
              draggable={isDraggable}
              onEdit={onEdit}
              onDelete={onDelete}
            />
          )
        )}
      </Table>
    </DragDropProvider>
  );
};
